import FormBox from '../../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import api from 'api';
import { Autocomplete, CircularProgress, InputAdornment, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BaseInput from 'components/common/BaseInput';
import ToggleButtonGroup from '../../common/ToggleButtonGroup/ToggleButtonGroup';
import IconTooltipTextField from 'components/common/IconTooltipTextField';
import { ReactComponent as InfoIcon } from 'assets/icons/information.svg';
import ReactGA from 'react-ga4';
import { formatNumber } from 'utils/formatting';
import * as S from '../Forms.styled';
import { useAuth } from 'contexts/ProtectedRoute';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { isSalesPersonRole } from 'data/constants/roles';
import { useTranslation } from 'react-i18next';
import { returnOptionsWithParameter, fetchOptionsReturn } from './utils';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    chosenCountryName,
    countryNameOptions,
    jobOfferNominationVisible,
    rowCountryEnabled,
} from 'utils/countries';

interface Entry {
    work_permit: string | null;
    profession: string | null;
    country: string | null;
    region: string | null;
    hourly_wage: number | null;
    nomination: string | null;
    zip: string | null;
}

const initEntry: Entry = {
    work_permit: '',
    profession: '',
    country: '',
    region: '',
    hourly_wage: 0,
    nomination: '',
    zip: '',
};

interface Msg {
    work_permit: string;
    profession: string;
    country: string;
    region: string;
    hourly_wage: string;
    nomination: string;
    zip: string;
}

const initMsg: Msg = {
    work_permit: '',
    profession: '',
    country: '',
    region: '',
    hourly_wage: '',
    nomination: '',
    zip: '',
};

const JobOfferForm = () => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const { codes } = useEnabledCountries();
    const tableName = 'JobOffer';
    const [data, setData] = useState<Entry[]>([]);
    const [validationMsg, setValidationMsg] = useState<Msg[]>([]);
    const [hasJobOffer, setHasJobOffer] = useState<string>(t('no', { lng: language }));

    const [isUserData, setIsUserData] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const countryOptions = countryNameOptions(codes, data[0]?.country);

    const [options, setOptions] = useState({
        countryProfessions: [''],
        eoiNoi: [''],
        workPermitOptions: [''],
        regionOptions: {} as { [key: string]: string[] },
    });

    useEffect(() => {
        if (isUserData) {
            setIsLoading(false);
        }
    }, [isUserData]);

    const loadOptions = async (country: string) => {
        const [professions, regions, workPermits, nominations] = await Promise.all([
            returnOptionsWithParameter(api?.assessment_options.getProfessions, country),
            fetchOptionsReturn(api?.assessment_options.getRegionOptions),
            fetchOptionsReturn(api?.assessment_options.getWorkPermitOptions),
            returnOptionsWithParameter(api?.assessment_options.getNominationOptions, country),
        ]);

        setOptions({
            countryProfessions: professions || [''],
            regionOptions: regions || {},
            workPermitOptions: workPermits || [''],
            eoiNoi: nominations || [''],
        });
    };

    const loadData = async (data: Entry[]) => {
        if (data && data.length | Object.keys(data).length) {
            const tmp = new Array(data.length).fill(initMsg);
            setValidationMsg(tmp);
            setData(data);
            if (data.length > 0) {
                setHasJobOffer(t('yes', { lng: language }));
                loadOptions(data[0].country as string);
            }
        }
        if (data == null || data) {
            setIsUserData(true);
        } else {
            setIsUserData(false);
        }
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + '/' + tableName}`,
            title: 'JobOfferForm',
        });
    }, []);

    const { role } = useAuth();
    const params = useParams();
    const candidate_id = Number(params.id);

    const isSalesPerson = isSalesPersonRole(role);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (isAuthenticated) {
            setIsLoading(true);
            setIsUserData(false);
            setData([]);
            isSalesPerson
                ? api.tableInput.fetchCandidateInputSales(loadData, tableName, candidate_id)
                : api.tableInput.fetchCandidateInput(loadData, tableName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate_id, isAuthenticated]);

    // FIELD VALIDATION
    const validate = () => {
        let validated = true;
        const temp = [...validationMsg];
        for (let i = 0; i < data.length; i++) {
            for (const key of Object.keys(initEntry)) {
                const value = data[i][key as keyof Entry];
                let msg = '';
                if (key === 'work_permit' || key === 'zip') continue;
                if (key === 'nomination' && !jobOfferNominationVisible(codes, data[i].country)) {
                    continue;
                }
                if (value === '' || value === 0) {
                    msg = t('requiredField', { lng: language });
                    validated = false;
                }
                temp[i] = { ...temp[i], [key]: msg };
            }
        }
        setValidationMsg(temp);
        return validated;
    };

    const validateSingleField = (idx: number, field: string, value: string | boolean | null) => {
        const temp = [...validationMsg];
        let msg = '';
        const nominationHidden =
            field === 'nomination' && !jobOfferNominationVisible(codes, data[idx]?.country);
        if (!value && field !== 'zip' && !nominationHidden) {
            msg = t('requiredField', { lng: language });
        }
        temp[idx] = { ...temp[idx], [field]: msg };
        setValidationMsg(temp);
    };

    const handleUpdate = (idx: number, field: string, value: string | null) => {
        validateSingleField(idx, field, value);
        const temp = [...data];
        if (field === 'country') {
            const defaultVal = "Don't know";
            const isAustralia = value === t('australia', { lng: language });
            const isUk = value === t('unitedKingdom', { lng: language });
            const isAusOrUK = isAustralia || isUk;
            temp[idx] = {
                ...data[idx],
                region: '',
                profession: '',
                zip: '',
                nomination: isAustralia ? '' : defaultVal,
                // nomination_uk: isUk ? '' : defaultVal,
                work_permit: isAusOrUK ? defaultVal : '',
            };
            // getProfessions(value as string);
            // getNominationOptions();
            loadOptions(value as string);
        }
        temp[idx] = { ...temp[idx], [field]: value };
        setData(temp);
    };

    const handleUpdateHasJobOffer = (value: string) => {
        if (value === t('yes', { lng: language })) {
            const country = chosenCountryName(codes) ?? '';
            setHasJobOffer(t('yes', { lng: language }));
            setData([{ ...initEntry, country }]);
            setValidationMsg([initMsg]);
            if (country) {
                loadOptions(country);
            }
        } else if (value === t('no', { lng: language })) {
            setHasJobOffer(t('no', { lng: language }));
            setData([]);
            setValidationMsg([]);
        }
    };

    const validateNumber = (idx: number, field: string, value: string | null) => {
        let validated = true;
        const temp = [...validationMsg];
        let msg = '';
        // if (!value) {
        //     msg = 'Field should not be left empty';
        //     validated = false;
        // } else if (isNaN(Number(value.replaceAll(',', '')))) {
        if (value && isNaN(Number(value.replaceAll(',', '')))) {
            msg = t('shouldBeANumber', { lng: language });
            validated = false;
        }
        temp[idx] = { ...temp[idx], [field]: msg };
        setValidationMsg(temp);
        return validated;
    };

    const handleNumberUpdate = (idx: number, field: string, value: string) => {
        // const filteredValue = value.replace(/[^0-9,\.]/g, '');
        if (validateNumber(idx, field, value)) {
            const valueWithoutSeparators = value.split(',').join('');
            const tmp = [...data];
            tmp[idx] = { ...data[idx], [field]: valueWithoutSeparators };
            setData(tmp);
        }
    };

    const offerCountry = data[0]?.country;
    const showAustralianPostcode = rowCountryEnabled(codes, offerCountry, 'AU');
    const showCanadianOfferFields = rowCountryEnabled(codes, offerCountry, 'CA');
    const showNominationDropdown =
        rowCountryEnabled(codes, offerCountry, 'AU') ||
        rowCountryEnabled(codes, offerCountry, 'UK');

    //not based on domain, base on the selected val
    // const country = data[0]?.country;
    // const isAustralia = country === 'Australia';

    return (
        <>
            {isLoading ? (
                <S.LoadingContainer>
                    <CircularProgress color="primary" />
                </S.LoadingContainer>
            ) : (
                <FormBox validation={validate} data={[data]} tableNames={[tableName]}>
                    <S.Title>{t('jobOfferEmployerEndorsement', { lng: language })}</S.Title>

                    <ToggleButtonGroup
                        customStyle={true}
                        options={[t('yes', { lng: language }), t('no', { lng: language })]}
                        name="has_job_offer"
                        label={t('validJobOffer', { lng: language })}
                        value={hasJobOffer}
                        onChange={(_: string, value: string) => handleUpdateHasJobOffer(value)}
                    />

                    {hasJobOffer === t('yes', { lng: language }) && (
                        <>
                            <S.GridSection>
                                <S.FlexColumn>
                                    <Autocomplete
                                        disablePortal
                                        value={data[0]?.country}
                                        id="residence"
                                        options={countryOptions}
                                        onChange={(_, value) => handleUpdate(0, 'country', value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={`*${t('country', { lng: language })}`}
                                                error={validationMsg[0].country !== ''}
                                            />
                                        )}
                                    />
                                    <S.ValidationMsg>{validationMsg[0].country}</S.ValidationMsg>
                                </S.FlexColumn>
                                <S.FlexColumn>
                                    <Autocomplete
                                        disablePortal
                                        id="region"
                                        value={data[0]?.region}
                                        options={
                                            options.regionOptions[data[0]?.country as string] || []
                                        }
                                        onChange={(_, v: string | null) =>
                                            handleUpdate(0, 'region', v)
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={`*${t('region', { lng: language })}`}
                                                error={validationMsg[0].region !== ''}
                                            />
                                        )}
                                    />
                                    <S.ValidationMsg>{validationMsg[0].region}</S.ValidationMsg>
                                </S.FlexColumn>
                                {showAustralianPostcode && (
                                    <BaseInput
                                        id="Zip"
                                        label={t('zipLabel', { lng: language })}
                                        type="text"
                                        value={data[0]?.zip ?? ''}
                                        onChange={(e) => {
                                            handleUpdate(0, 'zip', e.target.value);
                                        }}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip
                                                            title={t('zipTooltipJobOffer', {
                                                                lng: language,
                                                            })}
                                                            arrow
                                                            enterTouchDelay={0}
                                                            leaveTouchDelay={6000}
                                                        >
                                                            <InfoOutlinedIcon
                                                                sx={{
                                                                    color: '#686868',
                                                                    cursor: 'pointer',
                                                                    fontSize: 20,
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                                {showCanadianOfferFields && (
                                    <>
                                        <S.FlexColumn>
                                            <Autocomplete
                                                id="work_permit"
                                                value={data[0]?.work_permit}
                                                options={options.workPermitOptions}
                                                onChange={(_, v) =>
                                                    handleUpdate(0, 'work_permit', v)
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={`*${t('workPermit', {
                                                            lng: language,
                                                        })}`}
                                                        error={validationMsg[0].work_permit !== ''}
                                                    />
                                                )}
                                            />
                                            <S.ValidationMsg>
                                                {validationMsg[0].work_permit}
                                            </S.ValidationMsg>
                                        </S.FlexColumn>
                                        <span style={{ marginBottom: '25px' }}>
                                            <ToggleButtonGroup
                                                options={[
                                                    t('yes', { lng: language }),
                                                    t('no', { lng: language }),
                                                ]}
                                                name="nomination"
                                                label={t('doYouHaveNOI', { lng: language })}
                                                value={
                                                    data[0]?.nomination === 'I have an NOI'
                                                        ? t('yes', { lng: language })
                                                        : t('no', { lng: language })
                                                }
                                                onChange={(_: string, value: string) =>
                                                    handleUpdate(
                                                        0,
                                                        'nomination',
                                                        value === t('yes', { lng: language })
                                                            ? 'I have an NOI'
                                                            : "Don't have it"
                                                    )
                                                }
                                            />
                                        </span>
                                    </>
                                )}
                                {showNominationDropdown && (
                                    <S.FlexColumn>
                                        <Autocomplete
                                            id="nomination"
                                            value={data[0]?.nomination}
                                            options={options.eoiNoi}
                                            onChange={(_, v) => handleUpdate(0, 'nomination', v)}
                                            renderInput={(params) => (
                                                <IconTooltipTextField
                                                    {...params}
                                                    label={t('employerNomination', {
                                                        lng: language,
                                                    })}
                                                    iconEnd={<InfoIcon />}
                                                    endText={
                                                        <>
                                                            <S.ToolTipHeading>
                                                                {t('govtOrPrivateInstitution', {
                                                                    lng: language,
                                                                })}
                                                            </S.ToolTipHeading>

                                                            <S.ToolTipItem>
                                                                <b>EOI </b>– Expression Of Interest
                                                            </S.ToolTipItem>

                                                            <S.ToolTipItem>
                                                                <b> NOI</b> – Notification Of
                                                                Interest
                                                            </S.ToolTipItem>
                                                        </>
                                                    }
                                                />
                                            )}
                                        />
                                        <S.ValidationMsg>
                                            {validationMsg[0].nomination}
                                        </S.ValidationMsg>
                                    </S.FlexColumn>
                                )}
                                <S.FlexColumn>
                                    <S.TextField
                                        id="hourly_wage"
                                        label={`*${t('hourlyWageLocalCurrency', { lng: language })}`}
                                        type="text"
                                        value={formatNumber(
                                            data[0]?.hourly_wage ? data[0]?.hourly_wage : ''
                                        )}
                                        onChange={(e) => {
                                            handleNumberUpdate(0, 'hourly_wage', e.target.value);
                                        }}
                                        error={validationMsg[0].hourly_wage !== ''}
                                    />
                                    <S.ValidationMsg>
                                        {validationMsg[0]?.hourly_wage}
                                    </S.ValidationMsg>
                                </S.FlexColumn>
                            </S.GridSection>
                            <S.Text style={{ marginBottom: '0.75rem' }}>
                                {t('thisSectionIsImportant', { lng: language })} <br></br>
                                {t('exactProfession', { lng: language })}
                            </S.Text>
                            <S.GridSection>
                                <S.FlexColumn>
                                    <Autocomplete
                                        id="occupation"
                                        value={data[0]?.profession}
                                        disabled={data[0]?.country === ''}
                                        options={options.countryProfessions}
                                        onChange={(_, v) => handleUpdate(0, 'profession', v)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={`*${t('profession', { lng: language })}`}
                                                error={validationMsg[0].profession !== ''}
                                            />
                                        )}
                                    />
                                    <S.ValidationMsg>{validationMsg[0].profession}</S.ValidationMsg>
                                </S.FlexColumn>
                            </S.GridSection>
                        </>
                    )}
                </FormBox>
            )}
        </>
    );
};

export default JobOfferForm;
